import { getLinkByName } from '@/src/components/mobile/homepageFooter/footerLinks'
import type { AppDispatch } from '@/src/redux/store'
import type { GoogleUserData } from '@/src/types/auth'
import { logError } from '@/src/utils/logError'
import SendToMobileIcon from '@mui/icons-material/SendToMobile'
import {
    Alert,
    AlertColor,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    InputLabel,
    Link,
    Snackbar,
    Stack,
    SxProps,
    Theme,
    Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import React, { useState } from 'react'
import GoogleSignInButton from '../../../../GoogleSignInButton'
import ItineraryTextField from '../../../../itinerary/itinearyTextField'
import { CTLoginFlowBackButton } from './otpVerificationModal/CTLoginFlowBackButton'
import { CTLoginFlowHeadingsAndTabs } from './otpVerificationModal/CTLoginFlowheadingsAndTabs'
interface FormState {
    email: string
    password: string
    isEmailValid: boolean
    isPasswordValid: boolean
    rememberCheckedStatus: boolean
}

interface FormHandlers {
    onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    setRememberCheckedStatus: (checked: boolean) => void
    onForgotClick: () => void
    onSubmit: (e: React.FormEvent) => void
    onTabChange: (tab: 0 | 1) => void
}

interface StatusState {
    loading: boolean
    error: string | null
    success: string | null
}

interface SignInFormProps {
    isMobile: boolean
    form: FormState
    status: StatusState
    handlers: FormHandlers
    t: (key: string) => string
    showGoogleSignIn?: boolean
    onGoogleSignIn?: (userData: GoogleUserData) => void
    dispatch?: AppDispatch
    sx?: SxProps<Theme>
    setActiveTab: (tab: number) => void
    showCTLoginFlowFormFlyin: boolean
    onCTLoginFlowBackClick: () => void
}

const SignInForm: React.FC<SignInFormProps> = ({
    isMobile,
    form,
    status,
    handlers,
    t,
    showGoogleSignIn = true,
    onGoogleSignIn,
    dispatch,
    sx = {},
    setActiveTab,
    showCTLoginFlowFormFlyin,
    onCTLoginFlowBackClick
}) => {

    const pathname = usePathname()
    const locale = pathname.split('/')[1] || 'en'
    const [showPassword, setShowPassword] = useState(false)
    const [notification, setNotification] = useState<{
        open: boolean
        message: string
        severity: AlertColor
    }>({ open: false, message: '', severity: 'info' })
    const tFooter = useTranslations('footer')
    const tOtpModal = useTranslations('otpModal')
    // Toggle password visibility
    const handleClickShowPassword = () => setShowPassword(show => !show)
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
    }

    // Handle Google Sign-In success
    const handleGoogleSignInSuccess = (userData: GoogleUserData) => {
        onGoogleSignIn?.(userData)
    }

    // Handle Google Sign-In error
    const handleGoogleSignInError = (error: Error) => {
        logError('SignInForm:GoogleSignIn', error)
        showNotification(error.message || t('googleSignInError'), 'error')
    }

    // Show notification
    const showNotification = (message: string, severity: AlertColor = 'error') => {
        setNotification({ open: true, message, severity })
    }

    // Handle notification close
    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }))
    }

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        handlers.onSubmit(e)
    }

    return (
        <Box sx={sx}>
            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>

            {showCTLoginFlowFormFlyin && <Box mb={2}> <CTLoginFlowHeadingsAndTabs activeTab={1} setActiveTab={setActiveTab} />
            </Box>}

            <form onSubmit={handleSubmit}>


                <Stack spacing={1.2}>

                    <Box>
                        <InputLabel
                            shrink
                            htmlFor={`email`}
                            sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                        >
                            {showCTLoginFlowFormFlyin ? tOtpModal('cleartripLoginFlow.emailInputLabel') : t('email')}
                        </InputLabel>
                        <ItineraryTextField
                            id="email"
                            placeholder={showCTLoginFlowFormFlyin ? tOtpModal('cleartripLoginFlow.emailInputLabel') : t('email')}
                            type="email"
                            // variant="outlined"
                            autoComplete="email"
                            value={form.email}
                            onChange={handlers.onEmailChange}
                            error={!form.isEmailValid}
                            helperText={!form.isEmailValid ? t('invalidEmail') : ''}
                            disabled={status.loading}
                            sx={{
                                '& .MuiInputBase-input': { py: 0.5, px: 1.5, fontSize: 13, height: 26, borderRadius: 1 },
                                borderRadius: 1
                            }}
                        />
                    </Box>

                    <Box>
                        <InputLabel
                            shrink
                            htmlFor={`password`}
                            sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                        >
                            {showCTLoginFlowFormFlyin ? tOtpModal('cleartripLoginFlow.passwordInputLabel') : t('password')}
                        </InputLabel>
                        <ItineraryTextField
                            id="password"
                            placeholder={showCTLoginFlowFormFlyin ? tOtpModal('cleartripLoginFlow.passwordInputLabel') : t('password')}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            value={form.password}
                            onChange={handlers.onPasswordChange}
                            error={!form.isPasswordValid}
                            helperText={!form.isPasswordValid ? t('invalidPassword') : ''}
                            disabled={status.loading}
                            sx={{
                                '& .MuiInputBase-input': { py: 0.5, px: 1.5, fontSize: 13, height: 26, borderRadius: 1 },
                                borderRadius: 1
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',

                        }}
                    >
                        <FormControlLabel
                            sx={{
                                margin: 0,
                                padding: 0
                            }}
                            control={
                                <Checkbox
                                    checked={form.rememberCheckedStatus}
                                    onChange={e => handlers.setRememberCheckedStatus(e.target.checked)}
                                    disabled={status.loading}
                                    sx={{
                                        margin: 0,
                                        padding: 0,
                                        mr: 1
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" fontSize={isMobile ? 12 : 13} color="text.secondary">
                                    {showCTLoginFlowFormFlyin ? tOtpModal('cleartripLoginFlow.keepMeSignedIn') : t('keepMeSignedIn')}
                                </Typography>
                            }
                        />
                        <Button
                            onClick={handlers.onForgotClick}
                            disabled={status.loading}
                            sx={{
                                textTransform: 'none',
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                minWidth: 'auto',
                                p: 0
                            }}
                        >
                             {showCTLoginFlowFormFlyin ? tOtpModal('cleartripLoginFlow.forgotPassword') : `${t('forgotPassword')}?`}
                        </Button>
                    </Box>

                    <Box display={"flex"} flexDirection={"column"} justifyContent={"space-between"} gap={1} >

                        <Button
                            type="submit"
                            variant="customVariantPrimary"
                            sx={{ width: '100%', fontWeight: 600, fontSize: 16, marginTop: '8px', borderRadius: '4px' }}
                            disabled={status.loading}
                        >
                            {status.loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : showCTLoginFlowFormFlyin ? (
                                tOtpModal('cleartripLoginFlow.signIn')
                            ) : (
                                tOtpModal('continue')
                            )}
                        </Button>
                        {showGoogleSignIn && !showCTLoginFlowFormFlyin && (
                            <GoogleSignInButton
                                onSuccess={handleGoogleSignInSuccess}
                                onError={handleGoogleSignInError}
                                buttonText="Google"
                                fullWidth

                            />
                        )}
                        {!showCTLoginFlowFormFlyin && <Button
                            fullWidth
                            variant="customVariantPrimary"
                            onClick={() => setActiveTab(0)}
                            startIcon={<SendToMobileIcon style={{ width: '18px', height: '18px' }} />}
                            sx={{
                                height: 40,
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                color: 'text.primary',
                                border: '1px solid',
                                borderColor: 'grey.300',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                '&:hover': {
                                    backgroundColor: 'grey.50',
                                    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
                                }
                            }}
                        >
                            {tOtpModal('continueWithMobileNumber')}
                        </Button>
                        }
                    </Box>

                    {showCTLoginFlowFormFlyin && <CTLoginFlowBackButton onClick={onCTLoginFlowBackClick!} />}

                </Stack>
            </form>
        </Box>
    )
}

export default SignInForm
